import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import i18n from 'i18n-js';
import { formatAmount } from 'utils';
import { Grid, Container } from '@material-ui/core';
import { Box, Button } from '@mui/material';
import Layout from 'components/Layout';
import LoadingOverlay from 'components/LoadingOverlay';
import PaymentBanner from 'components/PaymentBanner';
import Title from 'components/Title';
import Logs from 'components/Logs';
import Comments from 'components/Comments';
import { AccountStatementActions } from '../../state/modules/accountStatement';
import { uiActions } from 'state/modules/UI';
import { useStyles } from './AccountStatementList.styled';
import { statusStyles } from './Common.styled';
import { FETCH_STATE } from 'constants';
import { useParams } from 'react-router-dom';
import { useStripePayment } from 'hooks/useStripePayment';
import { EntriesTable } from './EntriesTable';
import { ReservationsTable } from './ReservationsTable';
import { PaymentsTable } from './PaymentsTable';
import moment from 'moment';

const BANNER_MESSAGES = {
  success: () => i18n.t('account_statement.banners.success'),
  auth_failed: () => i18n.t('account_statement.banners.auth_failed'),
  auth_declined: () => i18n.t('account_statement.banners.auth_declined'),
  canceled: () => i18n.t('account_statement.banners.canceled'),
};

const BANNER_SEVERITY = {
  success: 'success',
  auth_failed: 'error',
  auth_declined: 'error',
  canceled: 'error',
};

const AccountStatementDetails = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const classes = useStyles();
  const statusClasses = statusStyles();
  const [isVerifying, setIsVerifying] = useState(false);
  const [bannerType, setBannerType] = useState(null);
  const [paymentsRefreshKey, setPaymentsRefreshKey] = useState(0);
  const { confirmPayment } = useStripePayment();
  // We use multiple selectors to avoid table rerender / consider using reselect.
  const statement = useSelector((state) => state?.accountStatement?.statement);
  const fetchingStatement = useSelector((state) => state?.accountStatement?.fetchingStatement);
  const localeFormat = useSelector((state) => state?.organizations?.localeFormat);

  useEffect(() => {
    dispatch(AccountStatementActions.fetchAccountStatementById(id));
  }, [id]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const redirectStatus = params.get('redirect_status');
    if (!redirectStatus) return;
    if (redirectStatus === 'succeeded') setBannerType('success');
    else if (redirectStatus === 'failed') setBannerType('auth_failed');
    else if (redirectStatus === 'canceled') setBannerType('canceled');
    window.history.replaceState({}, '', window.location.pathname);
  }, []);

  const statementAvailable = fetchingStatement === FETCH_STATE.DONE;
  const clientSecret = statement?.payment_intent_3ds?.client_secret;
  const paymentMethodId = statement?.payment_intent_3ds?.payment_method_id;
  const showVerifyButton = statementAvailable && !!clientSecret && statement?.status !== 'PAID';

  const handleVerifyPayment = async () => {
    setIsVerifying(true);
    try {
      const { error, paymentIntent } = await confirmPayment(clientSecret, paymentMethodId);
      if (error) {
        dispatch(AccountStatementActions.fetchAccountStatementById(id));
        setPaymentsRefreshKey((k) => k + 1);
        setIsVerifying(false);
        if (error.type) {
          const lastMsg = error.payment_intent?.last_payment_error?.message ?? '';
          if (error.code === 'card_declined') {
            setBannerType('auth_declined');
          } else if (lastMsg.toLowerCase().includes('canceled')) {
            setBannerType('canceled');
          } else {
            setBannerType('auth_failed');
          }
        } else {
          dispatch(
            uiActions.handleShowSnackbar({
              type: 'error',
              message: error.message || i18n.t('account_statement.payment_error'),
            })
          );
        }
        return;
      }
      if (paymentIntent?.status === 'succeeded') {
        dispatch(AccountStatementActions.fetchAccountStatementById(id));
        setPaymentsRefreshKey((k) => k + 1);
        setBannerType('success');
      }
      setIsVerifying(false);
    } catch {
      setIsVerifying(false);
    }
  };

  const invoiceDate = statementAvailable ? new Date(statement?.due_time) : null;
  const year =
    invoiceDate instanceof Date && !isNaN(invoiceDate)
      ? new Intl.DateTimeFormat('en-US', { year: 'numeric' }).format(invoiceDate)
      : '-';
  const month =
    invoiceDate instanceof Date && !isNaN(invoiceDate)
      ? new Intl.DateTimeFormat('en-US', { month: 'long' }).format(invoiceDate)
      : '-';
  const orgName = statement?.payer?.customer_name;
  const title = statementAvailable ? `${orgName}, ${month}, ${year}` : '--';

  return (
    <Layout title={i18n.t('account_statement.title')}>
      <Container className={classes.container}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid
              container
              spacing={2}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid item>
                <Title title={title} />
              </Grid>
              <Grid item>
                <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
                  {bannerType && (
                    <PaymentBanner
                      severity={BANNER_SEVERITY[bannerType]}
                      message={BANNER_MESSAGES[bannerType]()}
                      onClose={() => setBannerType(null)}
                    />
                  )}
                  {showVerifyButton && (
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={isVerifying}
                      onClick={handleVerifyPayment}
                      data-cy="btn-verify-payment"
                      sx={{
                        width: '220px',
                        height: '33px',
                        textTransform: 'none',
                        borderRadius: '4px',
                        fontWeight: 600,
                      }}
                    >
                      {i18n.t('account_statement.verify_and_complete_payment')}
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <div className={classes.detailsContainer}>
              <div>
                <span className={classes.detailsItem}>{i18n.t('common.due_date')}: </span>
                <span>{moment(statement?.due_time).format(localeFormat)}</span>
              </div>
              <div>
                <span className={classes.detailsItem}>{i18n.t('common.id')}: </span>
                <span>{statement?.id || '--'}</span>
                <span>{` | `}</span>
                <span className={classes.detailsItem}>{i18n.t('common.total')}: </span>
                <span>{statement?.total ? `$${formatAmount(statement?.total)}` : '--'}</span>
                <span>{` | `}</span>
                <span className={classes.detailsItem} style={{ textTransform: 'uppercase' }}>
                  {i18n.t('common.status')}:{' '}
                </span>
                <span className={statusClasses[statement.status]}>
                  {statement.status
                    ? i18n.t(`account_statement.statuses.${statement.status}`, {
                        defaultValue: statement.status,
                      })
                    : '--'}
                </span>
                <span>{` | `}</span>
                <span className={classes.detailsItem}>{i18n.t('common.partnership')}: </span>
                <span className={statusClasses[statement?.relationship?.status]}>
                  {statement?.relationship?.status
                    ? i18n.t(`partners.statuses.${statement?.relationship?.status}`, {
                        defaultValue: statement?.relationship?.status,
                      })
                    : '--'}
                </span>
              </div>
            </div>
          </Grid>

          <EntriesTable accountStatementId={id} />

          <ReservationsTable accountStatementId={id} />

          <PaymentsTable accountStatementId={id} refresh={paymentsRefreshKey} />
          <Grid item xs={4}>
            <Comments type="account_statement" id={+id} />
          </Grid>
          <Grid item xs={4}>
            <Logs
              targetId={parseInt(id)}
              targetType="AccountStatement"
              title={i18n.t('account_statement.logs')}
            />
          </Grid>
        </Grid>
      </Container>

      <LoadingOverlay
        open={isVerifying}
        message={i18n.t('account_statement.redirecting_to_stripe')}
      />
    </Layout>
  );
};

export { AccountStatementDetails };
